import { createUserScopedStorageAtom } from '@heidi/utils/jotai-storage';
import { atom } from 'jotai';
import { DICTATION_FEEDBACK_INCREASED_CADENCE_PROMPT_COUNT_STORAGE_KEY } from '../../contracts';
import { getDictationBubblePorts } from '../bubble/bubble-ports';
import {
  dictationCsatCadenceScaleAtom,
  dictationCsatCounterAtom,
  LEGACY_DICTATION_FEEDBACK_CADENCE,
  normalizeDictationFeedbackCadenceCounter,
  rollDictationCsatCadence,
} from '../bubble/csat/dictationCsatAtoms';

const EARLY_FEEDBACK_CADENCE = 10;
const MID_FEEDBACK_CADENCE = 15;
const PROMPTS_PER_INCREASED_CADENCE_STAGE = 3;
const PROMPTS_BEFORE_LEGACY_CADENCE = PROMPTS_PER_INCREASED_CADENCE_STAGE * 2;

/** Number of increased-cadence feedback prompts shown to the signed-in user. */
export const dictationFeedbackIncreasedCadencePromptCountAtom =
  createUserScopedStorageAtom<number>(
    DICTATION_FEEDBACK_INCREASED_CADENCE_PROMPT_COUNT_STORAGE_KEY,
    0
  );

const normalizePromptCount = (count: number): number =>
  Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;

const resolveIncreasedDictationFeedbackCadence = (promptCount: number): number => {
  if (promptCount < PROMPTS_PER_INCREASED_CADENCE_STAGE) {
    return EARLY_FEEDBACK_CADENCE;
  }
  if (promptCount < PROMPTS_BEFORE_LEGACY_CADENCE) {
    return MID_FEEDBACK_CADENCE;
  }
  return LEGACY_DICTATION_FEEDBACK_CADENCE;
};

const resolveDictationFeedbackCadence = async (promptCount: number): Promise<number> => {
  const ports = getDictationBubblePorts();
  if (
    !ports.isIncreasedFeedbackCadenceEnabled() ||
    !(await ports.resolveIsFreeNonManagedUser())
  ) {
    return LEGACY_DICTATION_FEEDBACK_CADENCE;
  }
  return resolveIncreasedDictationFeedbackCadence(normalizePromptCount(promptCount));
};

/**
 * Rolls the existing Dictation CSAT cadence for the after-dictating feedback
 * prompt without opening the legacy star-rating popover.
 *
 * The persisted counter is shared with the legacy survey so enabling or
 * disabling the feedback flag preserves the user's place in the cadence.
 */
export const dictationFeedbackPromptIncrementAtom = atom(null, async (get, set) => {
  const promptCount = normalizePromptCount(
    get(dictationFeedbackIncreasedCadencePromptCountAtom)
  );
  const cadence = await resolveDictationFeedbackCadence(promptCount);
  const current = normalizeDictationFeedbackCadenceCounter(
    get(dictationCsatCounterAtom),
    get(dictationCsatCadenceScaleAtom),
    cadence
  );
  const { nextCounter, shouldShow } = rollDictationCsatCadence(current, cadence);

  set(dictationCsatCadenceScaleAtom, cadence);
  set(dictationCsatCounterAtom, nextCounter);
  if (
    shouldShow &&
    cadence < LEGACY_DICTATION_FEEDBACK_CADENCE &&
    promptCount < PROMPTS_BEFORE_LEGACY_CADENCE
  ) {
    set(dictationFeedbackIncreasedCadencePromptCountAtom, promptCount + 1);
  }
  return shouldShow;
});

/** Starts the active cadence's cooldown after a prompt is completed or dismissed. */
export const dictationFeedbackPromptCooldownAtom = atom(null, (get, set) => {
  const previousCadence = get(dictationCsatCadenceScaleAtom);
  const cadence =
    previousCadence < LEGACY_DICTATION_FEEDBACK_CADENCE
      ? resolveIncreasedDictationFeedbackCadence(
          normalizePromptCount(get(dictationFeedbackIncreasedCadencePromptCountAtom))
        )
      : LEGACY_DICTATION_FEEDBACK_CADENCE;
  set(dictationCsatCadenceScaleAtom, cadence);
  set(dictationCsatCounterAtom, -cadence);
});
